import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CRM_PIPELINE_STAGES } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';
import {
  IconArrowLeft,
  IconArrowRight,
  IconBuilding,
  IconCheck,
  IconChevronRight,
  IconMessage,
  IconThumbsDown,
  IconUserPlus,
} from '@/features/marketplace-crm/icons';
import { ProspectForm, type ProspectFormValues } from '@/features/marketplace-crm/ProspectForm';
import { ProspectMessageDialog } from '@/features/marketplace-crm/ProspectMessageDialog';
import {
  displayNotes,
  isMarketplaceSource,
  leadDisplayName,
  loadCrmLeads,
  syncMarketplaceInquiriesToCrm,
  type CrmLead,
} from '@/features/marketplace-crm/syncInquiries';
import { syncWonLeadToCustomer } from '@/features/marketplace-crm/syncWonLeads';
import '@/features/marketplace-crm/marketplace-crm.css';

const PREV_STAGE: Record<number, string> = {
  1: 'nouveau',
  2: 'contacte',
  3: 'negociation',
};

const NEXT_STAGE: Record<number, string> = {
  0: 'contacte',
  1: 'negociation',
};

function displayIndex(status: string): number {
  if (status === 'nouveau') return 0;
  if (status === 'contacte') return 1;
  if (status === 'negociation') return 2;
  return 3;
}

export function CrmPage() {
  const { profile } = useAuth();
  const toast = useToast();
  const [leads, setLeads] = useState<CrmLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<CrmLead | null>(null);

  async function load() {
    if (!profile) return;
    const syncErr = await syncMarketplaceInquiriesToCrm(profile.id);
    if (syncErr) toast.error(syncErr);

    const { leads: rows, error: lErr } = await loadCrmLeads(profile.id);
    if (lErr) toast.error(lErr);
    else setLeads(rows);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, [profile]);

  async function moveStage(lead: CrmLead, newStage: string) {
    const { error: updateErr } = await supabase
      .from('crm_leads')
      .update({ status: newStage })
      .eq('id', lead.id);
    if (updateErr) {
      toast.error(updateErr.message);
      return;
    }

    if (newStage === 'gagne' && profile) {
      const syncErr = await syncWonLeadToCustomer({ ...lead, status: 'gagne' }, profile.id);
      if (syncErr) toast.error(syncErr);
    }

    await load();
  }

  async function saveProspect(values: ProspectFormValues): Promise<string | null> {
    if (!profile) return 'Session expirée.';
    const contact_name = `${values.first_name} ${values.last_name}`.trim();
    const payload = {
      user_id: profile.id,
      contact_name,
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      phone: values.phone,
      location: values.location,
      company: values.company,
      opportunity: values.product_interest,
      budget: values.value,
      status: values.stage || 'nouveau',
      source: 'manuel',
      priority: 'normal',
      notes: values.notes,
      project_id: null as string | null,
    };

    const stage = values.stage || 'nouveau';
    const { data: inserted, error: err } = await supabase.from('crm_leads').insert(payload).select('*').single();
    if (err) {
      const { data: fallbackInserted, error: fallbackErr } = await supabase
        .from('crm_leads')
        .insert({
          user_id: profile.id,
          contact_name,
          company: values.company,
          opportunity: values.product_interest,
          budget: values.value,
          status: stage,
          source: 'manuel',
          priority: 'normal',
          notes: values.notes,
          project_id: null,
        })
        .select('*')
        .single();
      if (fallbackErr) return fallbackErr.message;
      if (stage === 'gagne' && fallbackInserted) {
        const { leads } = await loadCrmLeads(profile.id);
        const lead = leads.find((l) => l.id === String(fallbackInserted.id));
        if (lead) await syncWonLeadToCustomer(lead, profile.id);
      }
    } else if (stage === 'gagne' && inserted) {
      const { leads } = await loadCrmLeads(profile.id);
      const lead = leads.find((l) => l.id === String(inserted.id));
      if (lead) await syncWonLeadToCustomer(lead, profile.id);
    }
    await load();
    return null;
  }

  const columns = useMemo(
    () =>
      CRM_PIPELINE_STAGES.map((stage) => {
        const items = leads.filter((l) => (stage.statuses as readonly string[]).includes(l.status));
        const totalValue = items.reduce((sum, p) => sum + (Number(p.budget) || 0), 0);
        return { ...stage, items, totalValue };
      }),
    [leads],
  );

  if (loading) {
    return (
      <div className="u-page mktcrm">
        <div className="crm-loading">
          <div className="crm-spinner" aria-label="Chargement" />
        </div>
      </div>
    );
  }

  return (
    <div className="u-page mktcrm crm-page">
      <div className="u-hero">
        <div>
          <p className="u-subtitle">Pipeline de conversion en mode processus</p>
        </div>
        <div className="u-actions">
          <Link to="/app/marketplace-crm" className="btn btn-ghost">
            ← Hub Marketplace & CRM
          </Link>
        </div>
      </div>

      <div className="crm-process-row">
        {columns.map((col, idx) => (
          <div key={col.key} className="crm-process-unit">
            <section className={`crm-stage crm-stage--${col.tone}`}>
              <header className="crm-stage-head">
                <div className="crm-stage-head-text">
                  <span className="crm-stage-step">
                    Étape {col.step} : {col.label}
                  </span>
                  <p className="crm-stage-desc">({col.description})</p>
                </div>
                <span className="crm-stage-count">{col.items.length}</span>
              </header>
              <div className="crm-stage-body">
                {col.items.map((p) => {
                  const dIdx = displayIndex(p.status);
                  const isGagne = p.status === 'gagne';
                  const isPerdu = p.status === 'perdu';
                  const marketplace = isMarketplaceSource(p.source);

                  return (
                    <article key={p.id} className="crm-prospect">
                      <div className="crm-prospect-top">
                        <p className="crm-prospect-name">{leadDisplayName(p)}</p>
                        {(isGagne || isPerdu) && (
                          <span className={`crm-outcome crm-outcome--${p.status}`}>
                            {isGagne ? <IconCheck size={12} /> : <IconThumbsDown size={12} />}
                            {isGagne ? 'Gagné' : 'Perdu'}
                          </span>
                        )}
                      </div>

                      <span className={`crm-source ${marketplace ? 'crm-source--mkt' : 'crm-source--man'}`}>
                        {marketplace ? 'Marketplace' : 'Manuel'}
                      </span>

                      {p.company ? (
                        <p className="crm-prospect-company">
                          <IconBuilding /> {p.company}
                        </p>
                      ) : null}

                      {p.opportunity ? <p className="crm-prospect-interest">{p.opportunity}</p> : null}

                      {p.budget > 0 ? (
                        <p className="crm-prospect-budget">{Number(p.budget).toLocaleString('fr-FR')} €</p>
                      ) : null}

                      {(p.email?.trim() || p.phone?.trim()) ? (
                        <button type="button" className="crm-msg-btn" onClick={() => setSelectedMessage(p)}>
                          <IconMessage /> Contacter
                        </button>
                      ) : null}

                      <div className="crm-nav-row">
                        <button
                          type="button"
                          className={`crm-nav-btn crm-nav-btn--${col.tone}`}
                          disabled={dIdx === 0}
                          aria-label="Étape précédente"
                          onClick={() => void moveStage(p, PREV_STAGE[dIdx]!)}
                        >
                          <IconArrowLeft />
                        </button>

                        {dIdx < 2 ? (
                          <button
                            type="button"
                            className={`crm-nav-btn crm-nav-btn--${col.tone}`}
                            aria-label="Étape suivante"
                            onClick={() => void moveStage(p, NEXT_STAGE[dIdx]!)}
                          >
                            <IconArrowRight />
                          </button>
                        ) : dIdx === 2 ? (
                          <div className="crm-nav-pair">
                            <button
                              type="button"
                              className="crm-nav-btn crm-nav-btn--win"
                              title="Marquer comme gagné"
                              aria-label="Gagné"
                              onClick={() => void moveStage(p, 'gagne')}
                            >
                              <IconCheck size={14} />
                            </button>
                            <button
                              type="button"
                              className="crm-nav-btn crm-nav-btn--lose"
                              title="Marquer comme perdu"
                              aria-label="Perdu"
                              onClick={() => void moveStage(p, 'perdu')}
                            >
                              <IconThumbsDown size={14} />
                            </button>
                          </div>
                        ) : (
                          <span className="crm-nav-spacer" />
                        )}
                      </div>
                    </article>
                  );
                })}

                {col.items.length === 0 ? <p className="crm-stage-empty">Aucun prospect</p> : null}

                {col.totalValue > 0 ? (
                  <p className="crm-stage-total">Total: {col.totalValue.toLocaleString('fr-FR')} €</p>
                ) : null}
              </div>
            </section>

            {idx < columns.length - 1 ? (
              <div className="crm-chevron" aria-hidden>
                <IconChevronRight />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      <div className="crm-add-wrap">
        <button type="button" className="crm-add-btn" onClick={() => setShowForm(true)}>
          <IconUserPlus /> Ajouter un prospect
        </button>
      </div>

      {showForm ? <ProspectForm onClose={() => setShowForm(false)} onSave={saveProspect} /> : null}

      {selectedMessage ? (
        <ProspectMessageDialog
          prospect={{
            first_name: selectedMessage.first_name,
            last_name: selectedMessage.last_name,
            contact_name: selectedMessage.contact_name,
            email: selectedMessage.email,
            phone: selectedMessage.phone,
            location: selectedMessage.location,
            company: selectedMessage.company,
            product_interest: selectedMessage.opportunity,
            notes: displayNotes(selectedMessage),
            message: displayNotes(selectedMessage),
          }}
          onClose={() => setSelectedMessage(null)}
        />
      ) : null}
    </div>
  );
}
